/**
 * useDemoMode — the "clever toggle".
 *
 * Precedence (highest wins):
 *   1. `?mode=demo` / `?mode=live` in the URL → persisted to localStorage, URL cleaned
 *   2. `localStorage.school_os_mode` set previously
 *   3. Backend `/api/config` `demo_mode` flag (server-authoritative)
 *   4. Hostname auto-detect (`demo.*` → demo)
 *   5. `REACT_APP_DEFAULT_MODE` env (defaults to "demo" during dev, "live" in prod builds)
 *
 * `toggle()` flips the mode and hard-reloads so both /login and every page re-renders cleanly.
 */
import { useCallback, useEffect, useState } from "react";
import axios from "axios";

const KEY = "school_os_mode";
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function hostnameMode() {
  if (typeof window === "undefined") return null;
  const h = window.location.hostname;
  if (h.startsWith("demo.")) return "demo";
  if (h.startsWith("app.") || h.startsWith("live.")) return "live";
  return null;
}

function readInitialMode() {
  // 1. URL param wins and is persisted, then stripped from the URL
  const url = new URL(window.location.href);
  const param = url.searchParams.get("mode");
  if (param === "demo" || param === "live") {
    localStorage.setItem(KEY, param);
    url.searchParams.delete("mode");
    window.history.replaceState({}, "", url.toString());
    return param;
  }
  // 2. localStorage
  const stored = localStorage.getItem(KEY);
  if (stored === "demo" || stored === "live") return stored;
  // 3. Hostname
  const host = hostnameMode();
  if (host) return host;
  // 4. Env fallback (default = demo for dev/preview; production sets this to "live" in .env)
  return (process.env.REACT_APP_DEFAULT_MODE || "demo").toLowerCase() === "live" ? "live" : "demo";
}

export function useDemoMode() {
  const [mode, setMode] = useState(() => (typeof window === "undefined" ? "demo" : readInitialMode()));
  const [serverMode, setServerMode] = useState(null);

  // Server view — for future audit / display of what the backend thinks
  useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/config`, { withCredentials: true })
      .then((r) => { if (!cancelled) setServerMode(r.data?.demo_mode ? "demo" : "live"); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  const toggle = useCallback((target) => {
    const next = target || (mode === "demo" ? "live" : "demo");
    localStorage.setItem(KEY, next);
    // Hard reload so the ENTIRE app re-reads the flag and re-renders login/badges cleanly.
    window.location.assign(window.location.pathname);
  }, [mode]);

  const clearOverride = useCallback(() => {
    localStorage.removeItem(KEY);
    window.location.assign(window.location.pathname);
  }, []);

  return {
    mode,
    isDemo: mode === "demo",
    isLive: mode === "live",
    serverMode,           // what backend reports (informational)
    serverIsDemo: serverMode === "demo",
    toggle,
    clearOverride,
  };
}
